import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './RoutePopup.module.css';

const ACTIVITY_TAGS = [
  'Hiking',
  'Scrambling',
  'Glacier travel',
  'Ice climbing',
  'Via ferrata'
];

const DIFFICULTIES = [
  'F', 'PD-', 'PD', 'PD+', 'AD-', 'AD', 'AD+',
  'D-', 'D', 'D+', 'TD-', 'TD', 'TD+',
  'ED-', 'ED', 'ED+', 'ABO'
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function RoutePopup({ onClose, onSubmit, mountains = [], initialMountainId = null }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [startPoint, setStartPoint] = useState('');
  const [startColor, setStartColor] = useState('#000000');
  const [hasStartColor, setHasStartColor] = useState(false);
  const [endPoint, setEndPoint] = useState('');
  const [waypoints, setWaypoints] = useState([{ name: '', color: '#000000', hasColor: false }]); 
  const [description, setDescription] = useState('');
  const [kms, setKms] = useState('');
  const [elevationGain, setElevationGain] = useState('');
  const [activityTags, setActivityTags] = useState([]);
  const [difficulty, setDifficulty] = useState('');
  const [routeType, setRouteType] = useState('one-day');
  const [seasonStart, setSeasonStart] = useState('');
  const [seasonEnd, setSeasonEnd] = useState('');
  const [gearProfile, setGearProfile] = useState('');
  
  // Tagging Mountains with Autocomplete
  const [selectedMountainTags, setSelectedMountainTags] = useState([]);
  const [mountainInputText, setMountainInputText] = useState('');
  const [mentionQuery, setMentionQuery] = useState(null);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [suggestions, setSuggestions] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initialMountainId) {
      const mt = mountains.find(m => String(m.id) === String(initialMountainId));
      if (mt) {
        // We set it as selected, but user might want to see it in the text or just as a tag?
        // User said "copy that to the route upload's tags too".
        // In PostPopup it's embedded in text. Here we have a specific "Tagged Mountains" field.
        // I will keep the field logic but make the input behave like the PostPopup one, 
        // OR the user wants the Route DESCRIPTION to support tags?
        // "Look at the popup for creating posts... I would like it to be integrated into the text... copy that to the route upload's tags too"
        // This likely means the "Tagged Mountains" input itself should be the text area where you type #?
        // But RoutePopup has a "name", "description". 
        // Currently `RoutePopup` has a specialized "Tagged Mountains (Required)" section with an input.
        // It seems the user wants the "Tagged Mountains" input to behave like the PostPopup text area but maybe just for adding tags?
        // "when I press enter, it selects the tag, please copy that to the route upload's tags too"
        // It sounds like replacing the current "search by typing" with the # based one.
        // Let's implement the # logic for the input field.
        setSelectedMountainTags([mt]);
      }
    }
  }, [initialMountainId, mountains]);

  const handleMountainInput = (e) => {
    const val = e.target.value;
    setMountainInputText(val);

    // Logic: Look for last word starting with #
    const selectionEnd = e.target.selectionEnd;
    let start = selectionEnd - 1;
    while (start >= 0 && !/\s/.test(val[start])) {
      start--;
    }
    start++; // Start of current word

    const word = val.slice(start, selectionEnd);
    if (word.startsWith('#')) {
      const query = word.slice(1).toLowerCase();
      setMentionQuery(query);
      setActiveSuggestion(0);

      const mMatches = mountains
        .filter(m => m.name.toLowerCase().includes(query))
        .filter(m => !selectedMountainTags.some(sel => sel.id === m.id)) // Exclude already selected?
        .slice(0, 5);
      
      setSuggestions(mMatches);
    } else {
      setMentionQuery(null);
      setSuggestions([]);
    }
  };

  const selectMountainTag = (m) => {
    // Current input state
    const val = mountainInputText;
    
    // Find where the #word is
    // We need to know where the caret was or roughly the last # token
    // Since this is a simple input for "adding tags", maybe we just clear the input?
    // "copy that to the route upload's tags too"
    // IF the user wants "integrated into the text", they might mean the Description field?
    // BUT they said "route upload's TAGS", which implies the tag selection mechanism.
    // The current UI has a dedicated tag input. I'll make THAT input support #.
    // And when you select, it ADDS the tag to the list and CLEARS the input (or the # part).
    
    // Actually, looking at the previous code, the input was `tagQuery`. 
    // If I make it text based:
    
    // Case 1: The input is just for searching tags. User types "#Everest", selects it. 
    // It gets added to `selectedMountainTags`. Input is cleared. This is standard "Tag Input".
    // Case 2: User types "Linking #Everest and #Lhotse". 
    // "integrated into the text" suggests Case 2. 
    // However, for Route Upload, we usually have a list of mountains.
    // I will assume the user wants the INPUT to allow typing #Name.
    
    // Let's implement: User types in the input. If they type #Name and select, it adds the tag pill 
    // AND replaces the text with just the name or clears it?
    // Usually tag inputs clear after selection.
    
    setSelectedMountainTags(prev => [...prev, m]);
    setMountainInputText(''); // Clear input after selection
    setMentionQuery(null);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (mentionQuery !== null && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestion(prev => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestion(prev => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectMountainTag(suggestions[activeSuggestion]);
      } else if (e.key === 'Escape') {
        setMentionQuery(null);
      }
    } else if (e.key === 'Enter') {
        // If simply pressing enter without suggestion, maybe try to match exact name?
        // Or prevent form submission
        e.preventDefault();
    }
  };


  const removeMountainTag = (id) => {
      setSelectedMountainTags(selectedMountainTags.filter(m => m.id !== id));
  };


  const handleWaypointChange = (index, field, value) => {
    const newWaypoints = [...waypoints];
    newWaypoints[index] = { ...newWaypoints[index], [field]: value };
    setWaypoints(newWaypoints);
  };

  const addWaypoint = () => {
    setWaypoints([...waypoints, { name: '', color: '#000000', hasColor: false }]);
  };

  const removeWaypoint = (index) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  };

  const handleActivityTagToggle = (tag) => {
    setActivityTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !startPoint.trim()) {
      alert(t('mountainPage.routes.errors.fillRequired'));
      return;
    }
    if (selectedMountainTags.length === 0) {
        alert("Please tag at least one mountain.");
        return;
    }

    setSubmitting(true);
    try {
      const routeData = {
        name,
        // type: 'return', // removed choice, defaulting to generic or calculating
        startPoint,
        startColor: hasStartColor ? startColor : null,
        endPoint: endPoint ? endPoint : startPoint, // Default to start if empty, or just send empty? User said "all routes should have an endpoint"
        waypoints: waypoints
          .filter(w => typeof w.name === 'string' && w.name.trim())
          .map(w => ({
            name: String(w.name).trim(),
            color: w.hasColor && w.color ? String(w.color).trim() : null
          })),
        description: description ? String(description).trim() : '',
        tags: Array.isArray(activityTags) ? activityTags : [],
        kms: kms ? parseFloat(kms) : null,
        elevationGain: elevationGain ? parseInt(elevationGain, 10) : null,
        difficulty,
        routeType,
        seasonStart,
        seasonEnd,
        gearProfile,
        mountainIds: selectedMountainTags.map(m => m.id)
      };
      console.log('RoutePopup submitting with difficulty:', difficulty, 'full data:', routeData);
      await onSubmit(routeData);
      onClose();
    } catch (err) {
      console.error(err);
      alert(t('mountainPage.routes.errors.save'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.popupOverlay}>
      <div className={styles.popup}>
        <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        <h2>{t('mountainPage.routes.addNew')}</h2>
        <form onSubmit={handleSubmit} className={styles.form}>
          
          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.name')}</label>
            <input 
              className={styles.input}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Standard Route via South Ridge"
              required
              maxLength={50}
            />
          </div>

          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.taggedMountains')}</label>
            <div className={styles.tagInputWrapper}>
                <div className={styles.selectedTags}>
                    {selectedMountainTags.map(m => (
                        <span key={m.id} className={styles.mountainTag}>
                            {m.name}
                            <button type="button" onClick={() => removeMountainTag(m.id)}>&times;</button>
                        </span>
                    ))}
                </div>
                <div style={{ position: 'relative' }}>
                    <input 
                        className={styles.input}
                        value={mountainInputText}
                        onChange={handleMountainInput}
                        onKeyDown={handleKeyDown}
                        placeholder={t('mountainPage.routes.tagPlaceholder')}
                    />
                     {mentionQuery !== null && suggestions.length > 0 && (
                        <div className={styles.suggestionsList}>
                            <div className={styles.suggestionHeader}>{t('mountainPage.routes.suggestions')}</div>
                            {suggestions.map((m, idx) => (
                                <div 
                                    key={m.id} 
                                    className={`${styles.suggestionItem} ${idx === activeSuggestion ? styles.suggestionItemActive : ''}`}
                                    onClick={() => selectMountainTag(m)}
                                >
                                    {m.name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.startPoint')}</label>
            <div className={styles.pointRow}>
                <input 
                  className={styles.input}
                  value={startPoint}
                  onChange={e => setStartPoint(e.target.value)}
                  placeholder={t('mountainPage.routes.startPointPlaceholder', { defaultValue: 'e.g. Parking lot at base' })}
                  required
                  maxLength={50}
                />
                <div className={styles.colorWrapper}>
                    <input 
                        type="checkbox"
                        checked={hasStartColor}
                        onChange={e => setHasStartColor(e.target.checked)}
                        title={t('mountainPage.routes.addMarkingColor')}
                    />
                    {hasStartColor && (
                        <input
                            type="color"
                            value={startColor}
                            onChange={e => setStartColor(e.target.value)}
                            className={styles.colorInput}
                        />
                    )}
                </div>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.waypointsLabel')}</label>
            <div className={styles.waypointsList}>
              {waypoints.map((wp, idx) => (
                <div key={idx} className={styles.waypointRow}>
                  <input 
                    className={styles.input}
                    value={wp.name}
                    onChange={e => handleWaypointChange(idx, 'name', e.target.value)}
                    placeholder={t('mountainPage.routes.waypointPlaceholder')}
                    maxLength={50}
                  />
                  <div className={styles.colorWrapper}>
                    <input 
                        type="checkbox"
                        checked={wp.hasColor}
                        onChange={e => handleWaypointChange(idx, 'hasColor', e.target.checked)}
                        title={t('mountainPage.routes.addMarkingColor')}
                    />
                    {wp.hasColor && (
                        <input
                            type="color"
                            value={wp.color}
                            onChange={e => handleWaypointChange(idx, 'color', e.target.value)}
                            className={styles.colorInput}
                        />
                    )}
                  </div>
                  {waypoints.length > 0 && (
                    <button 
                      type="button" 
                      className={styles.removeBtn}
                      onClick={() => removeWaypoint(idx)}
                      title={t('common.remove', { defaultValue: 'Remove' })}
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
              <button type="button" className={styles.addBtn} onClick={addWaypoint}>
                + {t('mountainPage.routes.addWaypoint')}
              </button>
            </div>
          </div>

          <div className={styles.formGroup}>
              <label>{t('mountainPage.routes.endPoint')}</label>
              <input 
                className={styles.input}
                value={endPoint}
                onChange={e => setEndPoint(e.target.value)}
                placeholder={t('mountainPage.routes.endPointPlaceholder', { defaultValue: 'e.g. Summit or other side' })}
                maxLength={50}
                required
              />
            </div>

          <div className={styles.formGroup} style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label>{t('mountainPage.routes.kms')}</label>
              <input
                className={styles.input}
                type="number"
                step="0.1"
                min="0"
                value={kms}
                onChange={e => setKms(e.target.value)}
                placeholder={t('mountainPage.routes.kmsPlaceholder', { defaultValue: 'e.g. 5.2' })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>{t('mountainPage.routes.elevationGain')}</label>
              <input
                className={styles.input}
                type="number"
                min="0"
                value={elevationGain}
                onChange={e => setElevationGain(e.target.value)}
                placeholder={t('mountainPage.routes.elevationGainPlaceholder', { defaultValue: 'e.g. 1200' })}
              />
            </div>
          </div>



          <div className={styles.formGroup} style={{ display: 'flex', gap: '1rem' }}>
             <div style={{flex: 1}}>
                <label>
                    {t('mountainPage.routes.difficulty')}
                    <span style={{ marginLeft: '0.5rem', fontWeight: 'normal', fontSize: '0.9rem', opacity: 0.8 }}>
                        ({t('mountainPage.routes.check')}
                        <a
                        href="https://www.bergfreunde.eu/alpine-grades-calculator/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.difficultyInfoLink}
                        title={t('mountainPage.details.difficultyInfo')}
                        style={{ marginLeft: '4px', color: 'inherit', textDecoration: 'none' }}
                        >
                        ?
                        </a>
                        )
                    </span>
                </label>
                <select 
                   className={styles.input}
                   value={difficulty}
                   onChange={e => setDifficulty(e.target.value)}
                >
                    <option value="">{t('mountainPage.routes.select')}</option>
                    {DIFFICULTIES.map(diff => (
                        <option key={diff} value={diff}>{diff}</option>
                    ))}
                </select>
             </div>
             <div style={{flex: 1}}>
                <label>{t('mountainPage.routes.type')}</label>
                <select 
                   className={styles.input}
                   value={routeType}
                   onChange={e => setRouteType(e.target.value)}
                >
                    <option value="one-day">{t('mountainPage.routes.types.oneDay')}</option>
                    <option value="multi-day-huts">{t('mountainPage.routes.types.multiDayHuts')}</option>
                </select>
             </div>
          </div>

          {/* New Fields: Season & Gear Profile */}
          <div className={styles.formGroup} style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label>{t('uploadMountain.fields.season')}</label>
                <div style={{ display: 'flex', gap: '5px' }}>
                    <select
                        className={styles.input}
                        value={seasonStart}
                        onChange={e => setSeasonStart(e.target.value)}
                    >
                         <option value="">Start</option>
                         {MONTHS.map(m => (
                             <option key={m} value={m}>{m}</option>
                         ))}
                    </select>
                    <select
                        className={styles.input}
                        value={seasonEnd}
                        onChange={e => setSeasonEnd(e.target.value)}
                    >
                         <option value="">End</option>
                         {MONTHS.map(m => (
                             <option key={m} value={m}>{m}</option>
                         ))}
                    </select>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                  <label>{t('uploadMountain.fields.gearProfile')}</label>
                  <select
                      className={styles.input}
                      value={gearProfile}
                      onChange={e => setGearProfile(e.target.value)}
                  >
                      <option value="">Select</option>
                      <option value="hiking">Hiking</option>
                      <option value="easy-alpinism">Easy Alpinism</option>
                      <option value="hard-alpinism">Hard Alpinism</option>
                      <option value="himalayan">Himalayan</option>
                  </select>
              </div>
          </div>

          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.description')}</label>
            <textarea 
              className={styles.textarea}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('mountainPage.routes.descriptionPlaceholder')}
              maxLength={1000}
            />
          </div>

          <div className={styles.formGroup}>
            <label>{t('mountainPage.routes.activityTags')}</label>
            <div className={styles.checkboxGroup}>
              {ACTIVITY_TAGS.map(tag => (
                <label key={tag} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={activityTags.includes(tag)}
                    onChange={() => handleActivityTagToggle(tag)}
                  />
                  {t(`mountainPage.routes.tags.${tag}`, { defaultValue: tag })}
                </label>
              ))}
            </div>
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              {t('action.cancel')}
            </button>
            <button type="submit" disabled={submitting} className={styles.submitBtn}>
              {submitting ? t('mountainPage.routes.submittingAdd') : t('mountainPage.routes.submitAdd')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
